import React, { useState, useEffect } from 'react';
import { Drawer, Input, Select, message } from 'antd';
import { createSample } from './api';
import { useMasters } from './MastersContext';

const REQUIRED_FIELDS = ['receiptDate', 'market', 'analysisType', 'productName', 'strength', 'stage', 'testName', 'priority'];

const todayStr = () => {
  const d = new Date();
  return d.toISOString().split('T')[0];
};

const initialForm = () => ({
  receiptDate: todayStr(),
  trfNumber: '',
  market: undefined,
  analysisType: undefined,
  productName: undefined,
  strength: '',
  batchNumber: '',
  stage: undefined,
  testName: undefined,
  stabilityCondition: undefined,
  duration: undefined,
  priority: 'Normal',
});

const NewSampleDrawer = ({ open, onClose, onCreated }) => {
  const { masters } = useMasters();
  const [form, setForm] = useState(initialForm());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initialForm());
      setErrors({});
    }
  }, [open]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const next = {};
    REQUIRED_FIELDS.forEach((f) => {
      const v = form[f];
      if (v === undefined || v === null || v === '') next[f] = 'Required';
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const data = await createSample({
        receiptDate: form.receiptDate,
        trfNumber: form.trfNumber || undefined,
        productName: form.productName,
        analysisType: form.analysisType,
        market: form.market,
        stage: form.stage,
        strength: form.strength,
        batchNumber: form.batchNumber || undefined,
        stabilityCondition: form.stabilityCondition || undefined,
        duration: form.duration || undefined,
        priority: form.priority,
        testName: form.testName,
      });
      message.success(`Sample ${data.arNumber} created — status: Pending`);
      onCreated && onCreated();
      onClose && onClose();
      setForm(initialForm());
    } catch (err) {
      message.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const masterOptions = (key) => {
    const items = masters[key] || [];
    if (items.length === 0) return [];
    return items.map((m) => ({ label: m.value, value: m.value }));
  };

  const emptyPlaceholder = (key) => {
    const items = masters[key] || [];
    return items.length === 0 ? 'No values — add in Masters tab' : 'Select';
  };

  const fieldBorder = (field) => (errors[field] ? '1px solid #dc2626' : '1px solid #d1d5db');

  const renderLabel = (text, required) => (
    <div style={{
      fontSize: 8, fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: 0.4, color: '#9ca3af', marginBottom: 2,
    }}>
      {text}
      {required && <span style={{ color: '#dc2626' }}> *</span>}
    </div>
  );

  const renderError = (field) =>
    errors[field] ? <div style={{ fontSize: 8, color: '#dc2626', marginTop: 1 }}>{errors[field]}</div> : null;

  const inputStyle = (field) => ({
    width: '100%', padding: '5px 7px',
    border: fieldBorder(field), borderRadius: 4,
    fontFamily: "'DM Sans', sans-serif", fontSize: 11,
    color: '#111827', background: errors[field] ? '#fef2f2' : '#fff',
    outline: 'none', transition: 'border-color 0.13s',
    boxSizing: 'border-box',
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={520}
      title={<span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>New Sample Entry</span>}
      styles={{
        header: { background: '#1a3353', borderBottom: 'none', padding: '11px 15px' },
        body: { padding: '12px 14px' },
        footer: { padding: '9px 14px', borderTop: '1px solid #d1d5db' },
      }}
      closeIcon={<span style={{ color: 'rgba(255,255,255,.6)', fontSize: 18 }}>×</span>}
      footer={
        <div style={{ display: 'flex', gap: 7 }}>
          <button onClick={onClose} style={{
            padding: '8px 14px', background: '#f3f4f6', color: '#4b5563',
            border: '1px solid #d1d5db', borderRadius: 4,
            fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, cursor: 'pointer',
          }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={submitting} style={{
            flex: 1, padding: 8, background: '#2563eb', color: '#fff',
            border: 'none', borderRadius: 4,
            fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, cursor: 'pointer',
            opacity: submitting ? 0.7 : 1,
          }}>
            {submitting ? 'Creating...' : 'Create Sample'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginBottom: 8 }}>
        {/* Row 1 */}
        <div>
          {renderLabel('Date Received', true)}
          <input
            type="date"
            value={form.receiptDate}
            onChange={(e) => set('receiptDate', e.target.value)}
            style={inputStyle('receiptDate')}
          />
          {renderError('receiptDate')}
        </div>
        <div>
          {renderLabel('TRF No.', false)}
          <input
            value={form.trfNumber}
            onChange={(e) => set('trfNumber', e.target.value)}
            style={inputStyle()}
            placeholder="Optional"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginBottom: 8 }}>
        {/* Row 2 */}
        <div>
          {renderLabel('Market', true)}
          <Select
            size="small"
            style={{ width: '100%' }}
            value={form.market}
            onChange={(v) => set('market', v)}
            options={masterOptions('markets')}
            placeholder={emptyPlaceholder('markets')}
            status={errors.market ? 'error' : undefined}
          />
          {renderError('market')}
        </div>
        <div>
          {renderLabel('Analysis Type', true)}
          <Select
            size="small"
            style={{ width: '100%' }}
            value={form.analysisType}
            onChange={(v) => set('analysisType', v)}
            options={masterOptions('analysisTypes')}
            placeholder={emptyPlaceholder('analysisTypes')}
            status={errors.analysisType ? 'error' : undefined}
          />
          {renderError('analysisType')}
        </div>
      </div>

      {/* Row 3 — full width product */}
      <div style={{ marginBottom: 8 }}>
        {renderLabel('Product', true)}
        <Select
          size="small"
          showSearch
          style={{ width: '100%' }}
          value={form.productName}
          onChange={(v) => set('productName', v)}
          options={masterOptions('products')}
          placeholder={emptyPlaceholder('products')}
          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          status={errors.productName ? 'error' : undefined}
        />
        {renderError('productName')}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginBottom: 8 }}>
        {/* Row 4 */}
        <div>
          {renderLabel('Strength', true)}
          <input
            value={form.strength}
            onChange={(e) => set('strength', e.target.value)}
            style={inputStyle('strength')}
            placeholder="e.g. 500mg"
          />
          {renderError('strength')}
        </div>
        <div>
          {renderLabel('Batch No.', false)}
          <input
            value={form.batchNumber}
            onChange={(e) => set('batchNumber', e.target.value)}
            style={inputStyle()}
            placeholder="Optional"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginBottom: 8 }}>
        {/* Row 5 */}
        <div>
          {renderLabel('Stage', true)}
          <Select
            size="small"
            style={{ width: '100%' }}
            value={form.stage}
            onChange={(v) => set('stage', v)}
            options={masterOptions('stages')}
            placeholder={emptyPlaceholder('stages')}
            status={errors.stage ? 'error' : undefined}
          />
          {renderError('stage')}
        </div>
        <div>
          {renderLabel('Test', true)}
          <Select
            size="small"
            showSearch
            style={{ width: '100%' }}
            value={form.testName}
            onChange={(v) => set('testName', v)}
            options={masterOptions('tests')}
            placeholder={emptyPlaceholder('tests')}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            status={errors.testName ? 'error' : undefined}
          />
          {renderError('testName')}
        </div>
      </div>

      {/* Section divider */}
      <div style={{
        fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
        color: '#9ca3af', padding: '5px 0 3px', borderTop: '1px solid #e5e7eb', marginBottom: 5,
      }}>
        Optional Fields
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginBottom: 8 }}>
        {/* Row 6 */}
        <div>
          {renderLabel('Stability Condition', false)}
          <Select
            size="small"
            allowClear
            style={{ width: '100%' }}
            value={form.stabilityCondition}
            onChange={(v) => set('stabilityCondition', v)}
            options={masterOptions('stabilityConditions')}
            placeholder={emptyPlaceholder('stabilityConditions')}
          />
        </div>
        <div>
          {renderLabel('Duration / Time Point', false)}
          <Select
            size="small"
            allowClear
            style={{ width: '100%' }}
            value={form.duration}
            onChange={(v) => set('duration', v)}
            options={masterOptions('durations')}
            placeholder={emptyPlaceholder('durations')}
          />
        </div>
      </div>

      {/* Row 7 — priority toggle */}
      <div style={{ marginBottom: 8 }}>
        {renderLabel('Priority', true)}
        <div style={{ display: 'flex', gap: 5 }}>
          <button
            type="button"
            onClick={() => set('priority', 'Normal')}
            style={{
              flex: 1, padding: 4, fontSize: 10, fontWeight: 600, textAlign: 'center',
              borderRadius: 4, cursor: 'pointer', transition: 'all 0.12s',
              border: form.priority === 'Normal' ? '1.5px solid #2563eb' : '1.5px solid #d1d5db',
              background: form.priority === 'Normal' ? '#eff6ff' : '#f9fafb',
              color: form.priority === 'Normal' ? '#2563eb' : '#4b5563',
              boxShadow: form.priority === 'Normal' ? '0 0 0 2px rgba(37,99,235,.2)' : 'none',
            }}
          >
            Normal
          </button>
          <button
            type="button"
            onClick={() => set('priority', 'Urgent')}
            style={{
              flex: 1, padding: 4, fontSize: 10, fontWeight: 600, textAlign: 'center',
              borderRadius: 4, cursor: 'pointer', transition: 'all 0.12s',
              border: form.priority === 'Urgent' ? '1.5px solid #ea580c' : '1.5px solid #d1d5db',
              background: form.priority === 'Urgent' ? '#fff7ed' : '#f9fafb',
              color: form.priority === 'Urgent' ? '#ea580c' : '#4b5563',
              boxShadow: form.priority === 'Urgent' ? '0 0 0 2px rgba(234,88,12,.2)' : 'none',
            }}
          >
            ★ Urgent
          </button>
        </div>
        {renderError('priority')}
      </div>
    </Drawer>
  );
};

export default NewSampleDrawer;
